import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getOrderDeatilsDB } from 'slice/orderSliceDB';
import logo from '../../asserts/img/tomoyokartlogo.png';

const getStatusLabel = (status) => {
  const labels = {
    pending: 'Pending',
    in_progress: 'In Progress',
    shipped: 'Shipped',
    delivered: 'Delivered',
    completed: 'Completed',
    cancelByUser: 'Cancelled By User',
    reject: 'Rejected',
  };

  return labels[status] || status || 'Pending';
};

const getStatusBadgeStyle = (status) => {
  switch (status) {
    case 'pending':
      return { backgroundColor: '#f59e0b' };
    case 'in_progress':
      return { backgroundColor: '#0ea5e9' };
    case 'shipped':
      return { backgroundColor: '#2563eb' };
    case 'delivered':
    case 'completed':
      return { backgroundColor: '#059669' };
    case 'cancelByUser':
    case 'reject':
      return { backgroundColor: '#ef4444' };
    default:
      return { backgroundColor: '#64748b' };
  }
};

const formatCurrency = (value) => `₹${Number(value || 0).toFixed(2)}`;

const formatDate = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('en-IN', {
    dateStyle: 'medium',
  });
};

const BillDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const printRef = useRef(null);
  const cartData = useSelector((state) => state.orderDB);
  const orderData = cartData.data || {};
  const items = orderData.itemData1 || [];

  useEffect(() => {
    dispatch(getOrderDeatilsDB(id));
  }, [dispatch, id]);

  const calculateTotal = () =>
    items.reduce((acc, item) => acc + Number(item?.price || 0) * Number(item?.quantity || 0), 0);

  const totalAmount = calculateTotal();
  const invoiceDate = orderData?.order?.createdAt || orderData?.items?.[0]?.createdAt;

  const handlePrint = () => {
    const printContents = printRef.current?.innerHTML;
    const printWindow = window.open('', '', 'width=1100,height=750');

    if (!printContents || !printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice</title>
          <style>
            * { box-sizing: border-box; }
            body {
              margin: 0;
              padding: 32px;
              font-family: Arial, sans-serif;
              background: #ffffff;
              color: #1f2937;
            }
            .invoice-shell {
              max-width: 980px;
              margin: 0 auto;
              border: 1px solid #e5e7eb;
              border-radius: 18px;
              padding: 28px;
            }
            .invoice-top {
              display: flex;
              justify-content: space-between;
              gap: 24px;
              margin-bottom: 24px;
            }
            .invoice-logo {
              width: 170px;
              height: auto;
            }
            .invoice-title {
              margin: 0 0 10px;
              font-size: 34px;
              font-weight: 700;
              color: #a1572f;
            }
            .invoice-meta p,
            .invoice-billing p,
            .invoice-from p {
              margin: 4px 0;
              font-size: 14px;
            }
            .status-badge {
              display: inline-block;
              margin-top: 10px;
              padding: 8px 14px;
              border-radius: 999px;
              color: #ffffff;
              font-size: 12px;
              font-weight: 700;
            }
            .invoice-billing-wrap {
              display: flex;
              justify-content: space-between;
              gap: 24px;
              border-top: 1px solid #e5e7eb;
              border-bottom: 1px solid #e5e7eb;
              padding: 18px 0;
              margin-bottom: 24px;
            }
            .invoice-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 14px;
            }
            .invoice-table th,
            .invoice-table td {
              border: 1px solid #d1d5db;
              padding: 12px;
            }
            .invoice-table th {
              background: #a1572f;
              color: #ffffff;
              text-align: left;
            }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .summary-row {
              background: #f3f4f6;
              font-weight: 600;
            }
            .summary-total {
              background: #e5e7eb;
              font-weight: 700;
            }
            .thanks {
              margin-top: 24px;
              text-align: center;
              font-weight: 700;
              color: #a1572f;
            }
          </style>
        </head>
        <body>
          ${printContents}
          <p class="thanks">Thanks for shopping with Tomoyokart!</p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div style={{ backgroundColor: '#f8f4ef', minHeight: '100vh', padding: '32px 16px' }}>
      <div className="container-fluid">
        <div className="d-flex justify-content-end mb-4">
          <button
            onClick={handlePrint}
            className="btn"
            style={{
              backgroundColor: '#a1572f',
              color: '#fff',
              borderRadius: '10px',
              padding: '10px 18px',
              fontWeight: 600,
            }}
          >
            Print Invoice
          </button>
        </div>

        <div
          ref={printRef}
          className="invoice-shell bg-white shadow-sm mx-auto"
          style={{
            maxWidth: '1100px',
            borderRadius: '22px',
            border: '1px solid #e5e7eb',
            padding: '32px',
          }}
        >
          <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
            <div className="mb-3">
              <img src={logo} alt="Tomoyokart logo" style={{ width: '180px', maxWidth: '100%' }} />
            </div>
            <div className="text-md-right">
              <h2 style={{ color: '#a1572f', fontSize: '2rem', fontWeight: 700, marginBottom: '10px' }}>
                Invoice
              </h2>
              <p className="mb-1" style={{ color: '#4b5563' }}>
                <strong>Invoice ID:</strong> #{orderData?.order?.order_id || id}
              </p>
              <p className="mb-1" style={{ color: '#6b7280' }}>
                <strong>Date:</strong> {formatDate(invoiceDate)}
              </p>
              <span
                style={{
                  ...getStatusBadgeStyle(orderData?.order?.status),
                  color: '#fff',
                  display: 'inline-block',
                  padding: '8px 14px',
                  borderRadius: '999px',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  marginTop: '10px',
                }}
              >
                {getStatusLabel(orderData?.order?.status)}
              </span>
            </div>
          </div>

          <div
            className="d-flex flex-wrap justify-content-between"
            style={{
              borderTop: '1px solid #e5e7eb',
              borderBottom: '1px solid #e5e7eb',
              padding: '20px 0',
              marginBottom: '24px',
              gap: '24px',
            }}
          >
            <div>
              <h5 style={{ fontWeight: 600, color: '#374151', marginBottom: '10px' }}>Billing To:</h5>
              <p className="mb-1" style={{ color: '#4b5563' }}>
                {orderData?.customerData?.name || '-'}
              </p>
              <p className="mb-1" style={{ color: '#4b5563' }}>
                {orderData?.order?.shipping_address || orderData?.customerData?.address || '-'}
              </p>
              <p className="mb-1" style={{ color: '#4b5563' }}>
                Mobile: {orderData?.customerData?.mobile || '-'}
              </p>
            </div>

            <div className="text-md-right">
              <h5 style={{ fontWeight: 600, color: '#374151', marginBottom: '10px' }}>From:</h5>
              <p className="mb-1" style={{ color: '#a1572f', fontWeight: 800 }}>Tomoyokart</p>
              <p className="mb-1" style={{ color: '#4b5563' }}>India</p>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table mb-0" style={{ border: '1px solid #d1d5db' }}>
              <thead>
                <tr style={{ backgroundColor: '#a1572f', color: '#fff' }}>
                  <th style={{ padding: '14px', borderColor: '#d1d5db' }}>Product Name</th>
                  <th className="text-center" style={{ padding: '14px', borderColor: '#d1d5db' }}>
                    Quantity
                  </th>
                  <th className="text-right" style={{ padding: '14px', borderColor: '#d1d5db' }}>
                    Price
                  </th>
                  <th className="text-right" style={{ padding: '14px', borderColor: '#d1d5db' }}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.length ? (
                  items.map((item, index) => {
                    const quantity = Number(item?.quantity || 0);
                    const price = Number(item?.price || 0);
                    const lineTotal = price * quantity;

                    return (
                      <tr key={`${item?.id || item?.product?.id || 'item'}-${index}`}>
                        <td style={{ padding: '14px', borderColor: '#d1d5db', color: '#374151' }}>
                          {item?.product?.name || '-'}
                        </td>
                        <td
                          className="text-center"
                          style={{ padding: '14px', borderColor: '#d1d5db', color: '#374151' }}
                        >
                          {quantity}
                        </td>
                        <td
                          className="text-right"
                          style={{ padding: '14px', borderColor: '#d1d5db', color: '#374151' }}
                        >
                          {formatCurrency(price)}
                        </td>
                        <td
                          className="text-right"
                          style={{ padding: '14px', borderColor: '#d1d5db', color: '#111827', fontWeight: 600 }}
                        >
                          {formatCurrency(lineTotal)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center"
                      style={{ padding: '18px', borderColor: '#d1d5db', color: '#6b7280' }}
                    >
                      No invoice items found.
                    </td>
                  </tr>
                )}

                <tr style={{ backgroundColor: '#f3f4f6', fontWeight: 600 }}>
                  <td colSpan={3} style={{ padding: '14px', borderColor: '#d1d5db' }}>
                    Sub Total
                  </td>
                  <td className="text-right" style={{ padding: '14px', borderColor: '#d1d5db' }}>
                    {formatCurrency(totalAmount)}
                  </td>
                </tr>
                <tr style={{ backgroundColor: '#e5e7eb', fontWeight: 700 }}>
                  <td colSpan={3} style={{ padding: '14px', borderColor: '#d1d5db' }}>
                    Total Amount
                  </td>
                  <td className="text-right" style={{ padding: '14px', borderColor: '#d1d5db', color: '#111827' }}>
                    {formatCurrency(totalAmount)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="text-center mt-4" style={{ color: '#a1572f', fontWeight: 700 }}>
            Thanks for shopping with Tomoyokart!
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillDetails;
